
import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface FareHarborCalendarProps {
  itemId?: number;
  flowId?: number;
  className?: string;
}

const FareHarborCalendar: React.FC<FareHarborCalendarProps> = ({ 
  itemId, 
  flowId = 1219724, 
  className = '' 
}) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  
  // Construct the iframe URL with proper parameters
  let iframeUrl = 'https://fareharbor.com/embeds/calendar/catsabouttown/?fallback=simple&full-items=yes';
  
  // Add flowId if provided
  if (flowId) {
    iframeUrl += `&flow=${flowId}`;
  }
  
  // Add itemId if provided
  if (itemId) {
    iframeUrl += `&items=${itemId}`;
  }
  
  const handleIframeLoad = () => {
    setIsLoading(false);
  };
  
  const handleIframeError = () => {
    setIsLoading(false);
    setLoadError('Failed to load the booking calendar. Please try again later.');
    
    toast({
      title: 'Calendar Error',
      description: 'Failed to load the booking calendar. Please try again later.',
      variant: 'destructive'
    });
  };
  
  return (
    <div className={`fare-harbor-container ${className}`} role="region" aria-label="Tour Booking Calendar">
      {isLoading && (
        <div className="flex flex-col items-center justify-center min-h-[400px]" aria-live="polite">
          <Loader2 className="h-12 w-12 text-primary animate-spin mb-4" aria-hidden="true" />
          <p className="text-primary font-medium">Loading calendar...</p>
        </div>
      )}
      
      {loadError && !isLoading && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 min-h-[200px] flex flex-col items-center justify-center" aria-live="assertive">
          <p className="font-semibold mb-2">Calendar could not be loaded</p>
          <p>{loadError}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-4 bg-red-100 hover:bg-red-200 text-red-800 font-medium py-2 px-4 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Try again
          </button>
          <p className="mt-4 text-sm">
            Alternatively, you can book directly on our{' '}
            <a 
              href="https://fareharbor.com/embeds/book/catsabouttowntours/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-red-800 font-medium underline"
            >
              FareHarbor booking page
            </a>
          </p>
        </div>
      )}
      
      <iframe 
        src={iframeUrl}
        className={`w-full min-h-[700px] border-0 ${isLoading ? 'hidden' : 'block'}`}
        onLoad={handleIframeLoad}
        onError={handleIframeError}
        title="FareHarbor Booking Calendar"
        aria-busy={isLoading}
      ></iframe>
    </div>
  );
};

export default FareHarborCalendar;
